import { useQuery } from "@tanstack/react-query"
import { Button } from "@workspace/web-ui/components/button"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@workspace/web-ui/components/sheet"
import { Skeleton } from "@workspace/web-ui/components/skeleton"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@workspace/web-ui/components/dialog"
import { cn } from "@workspace/web-ui/lib/utils"
import { Check, Circle, Info, Loader2, X, ChevronDown, ChevronUp, Plus } from "lucide-react"
import { toast } from "sonner"
import { useApproveRefillMutation, useRejectRefillMutation } from "../../api/refills"
import { getRefillsByIdQueryOptions } from "../../api/refills/query"
import { PatientCard } from "./patient-card"
import { RefillCard } from "./refill-card"
import { useState } from "react"
import { getRouteApi } from "@tanstack/react-router"
import { useSendInvoiceMutation } from "../../pages/encounters/api/mutations"
import DosespotButton from "../../pages/patient-logs/pages/dosespotModal"
import { formatDate } from "~/utils"
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@workspace/web-ui/components/select"
import { ImagesDocsSection, ImagesDocsSectionWhite } from "./images-docs-section"
import { Badge } from "@workspace/web-ui/components/badge"
import { PrescriptionModal } from "../../pages/patient-logs/components/modals/prescription-modal"
import { ResolveToggle } from "../resolve-toggle"

interface RefillSheetProps {
    refillId: string
    open: boolean
    onOpenChange: (open: boolean) => void
    recommendedTasks?: any[]
    userDosespotId?: string
    sessionId?: string
    encounterId?: string
    urgent_care_onboarded?: boolean
}

interface ImageModalProps {
    isOpen: boolean;
    onClose: () => void;
    imageUrl: string;
    imageIndex: number;
    totalImages: number;
}

const ImageModal = ({ isOpen, onClose, imageUrl, imageIndex, totalImages }: ImageModalProps) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl w-full max-h-[90vh] xl:max-h-[95vh] p-0">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="text-lg font-semibold">
                        Image {imageIndex + 1} of {totalImages}
                    </DialogTitle>
                </DialogHeader>
                <div className="p-6 pt-4">
                    <div className="relative">
                        <img
                            src={imageUrl}
                            alt={`Encounter Image ${imageIndex + 1}`}
                            className="w-full h-auto max-h-[70vh] object-contain rounded-md"
                        />
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const MedicalRefillsSheet = ({ refillId, userDosespotId, sessionId, encounterId, urgent_care_onboarded, open, onOpenChange, recommendedTasks = [] }: RefillSheetProps) => {

    const approveRefillMutation = useApproveRefillMutation({
        refill_id: refillId
    })

    const rejectRefillMutation = useRejectRefillMutation()

    const sendInvoiceMutation = useSendInvoiceMutation()

    const { data, isLoading } = useQuery(getRefillsByIdQueryOptions({
        id: refillId
    }))

    const routeApi = getRouteApi("/(dashboard)/dashboard/$program");
    const { program } = routeApi.useParams();

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejectDetails, setRejectDetails] = useState("");
    const [reasonError, setReasonError] = useState("");
    // const [instructions, setInstructions] = useState("");
    const [selectedImage, setSelectedImage] = useState<{ url: string; index: number } | null>(null);
    const [expandedRefills, setExpandedRefills] = useState<Set<string>>(new Set());
    const [rejectingRefillId, setRejectingRefillId] = useState<string | null>(null);
    const [rejectingMedication, setRejectingMedication] = useState<{ name: string; dosage: string } | null>(null);
    const [openPrescriptionModal, setOpenPrescriptionModal] = useState(false);

    const handleImageClick = (imageUrl: string, index: number) => {
        setSelectedImage({ url: imageUrl, index });
    };

    const handleCloseImageModal = () => {
        setSelectedImage(null);
    };

    const toggleRefillExpansion = (refillId: string) => {
        setExpandedRefills(prev => {
            // If clicking on an already expanded refill, close it
            if (prev.has(refillId)) {
                return new Set();
            }
            // Otherwise, close all others and open only this one (accordion behavior)
            return new Set([refillId]);
        });
    };

    const handleApproveRefill = async () => {
        try {

            // If it's weight-loss program and user has paid for the refill, send invoice to pharmacy
            if (program === "weight-loss" && data && data.paymentStatus === "paid") {
                try {
                    await sendInvoiceMutation.mutateAsync({
                        patient_id: data.patientId,
                        type_id: refillId,
                        type: "Refill"
                    });
                } catch (invoiceError) {
                    // Don't fail the whole operation if invoice sending fails
                }
            }

            toast.success('Refill Request Approved Successfully', {
                style: {
                    background: "#10B981",
                    color: "#FFFFFF",
                    borderColor: "#10B981",
                    '& svg': {
                        color: '#FFFFFF'
                    }
                } as React.CSSProperties
            });

            onOpenChange(false);
        } catch (error) {
            return toast.error(
                'Failed to approve refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    }


    const handleRejectRefill = async (reason: string, details: string, specificRefillId?: string) => {
        try {
            const targetRefillId = specificRefillId ?? refillId;
            const response = await rejectRefillMutation.mutateAsync({
                refill_id: targetRefillId,
                reason,
                details,
            });

            if (response.status === "success") {
                toast.success('Refill Request Rejected Successfully', {
                    style: {
                        background: "#10B981",
                        color: "#FFFFFF",
                        borderColor: "#10B981",
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                });

                // If rejecting a specific refill in a batch, don't close the whole sheet
                if (!specificRefillId) {
                    onOpenChange(false);
                }
            }
        } catch (error) {
            return toast.error(
                'Failed to reject refill request.',
                {
                    style: {
                        background: '#DC2626',
                        color: '#FFFFFF',
                        borderColor: '#DC2626',
                        '& svg': {
                            color: '#FFFFFF'
                        }
                    } as React.CSSProperties
                }
            );
        }
    }

    const openRejectModal = (refillId?: string, medication?: { name: string; dosage: string }) => {
        setRejectingRefillId(refillId || null);
        setRejectingMedication(medication || null);
        setShowRejectModal(true);
    }

    const closeRejectModal = () => {
        setShowRejectModal(false);
        setRejectingRefillId(null);
        setRejectingMedication(null);
        setRejectReason("");
        setRejectDetails("");
        setReasonError("");
    }

    const handleAddMedication = () => {
        setOpenPrescriptionModal(true);
    };

    // Determine if "Send to Pharmacy" button should be disabled based on tasks
    const shouldDisableSendToPharmacy = () => {
        const tasks = recommendedTasks ?? [];

        // If there are no tasks, button should be enabled
        if (!Array.isArray(tasks) || tasks.length > 0) {
            return false;
        }

        // If there are tasks, check if any are completed (paid for)
        const hasCompletedTask = tasks.some((task: any) => task?.status === "completed");

        // If all tasks are pending (none completed), disable the button
        // If at least one task is completed, enable the button
        return !hasCompletedTask;
    };

    // Check if any task has is_erx_sent === true to determine button text
    const shouldShowResendText = () => {
        const tasks = recommendedTasks ?? [];
        return Array.isArray(tasks) && tasks.length > 0 && tasks.some((task: any) => task?.is_erx_sent === true);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                className={cn(
                    "flex flex-col h-full gap-0 py-2 px-4 sm:max-w-[640px] w-full",
                    "focus:outline-none"
                )}
                aria-labelledby="refill-sheet-title"
                data-testid="refill-sheet"
            >
                <SheetHeader>
                    <div className="flex items-center justify-between">
                        <SheetTitle className="text-2xl font-semibold mb-4">
                            Medication Refill
                        </SheetTitle>
                        <ResolveToggle
                            encounterId={encounterId}
                            recommendedTasks={recommendedTasks}
                            disabled={!(Array.isArray(recommendedTasks) && recommendedTasks.length > 0)}
                            disabledReason="No medication available"
                            onSuccess={() => onOpenChange(false)}
                        />
                    </div>
                </SheetHeader>

                <div className="space-y-2 flex flex-col overflow-y-auto hide-scrollbar flex-1">
                    {isLoading ? (
                        <div className="space-y-4">
                            {/* PatientCard Skeleton */}
                            <div className="p-4 border rounded-lg flex flex-col gap-2">
                                <Skeleton className="h-6 w-1/3 mb-2" /> {/* Patient Name */}
                                <Skeleton className="h-4 w-1/2" /> {/* Program Enrollment */}
                                <Skeleton className="h-4 w-1/4" /> {/* Request Date */}
                                <Skeleton className="h-4 w-1/4" /> {/* Current Weight */}
                                <Skeleton className="h-4 w-1/4" /> {/* Patient ID */}
                            </div>
                            {/* RefillCard Skeleton */}
                            <div className="p-4 border rounded-lg flex flex-col gap-2">
                                <Skeleton className="h-6 w-1/2 mb-2" /> {/* Medication Name */}
                                <Skeleton className="h-4 w-1/3" /> {/* Dosage */}
                                <Skeleton className="h-4 w-1/3" /> {/* Refills */}
                            </div>
                        </div>
                    ) : (
                        <>
                            {data && (
                                <>
                                    <PatientCard
                                        patientName={data.patientName}
                                        patientEmail={data.patientEmail}
                                        programEnrollment={data.programEnrollment}
                                        requestDate={`${formatDate(data.requestedAt)}`}
                                        currentWeight={`${data.weightHistory.currentWeight} lbs`}
                                        patientId={data.patientId}
                                    />

                                    {/* Render batch refills with collapsible cards or single refill */}
                                    {data.isBatch && Array.isArray(data.medications) ? (
                                        // Batch refills - collapsible cards
                                        <div className="space-y-3">
                                            {data.medications.map((medication: any, index: number) => {
                                                const isExpanded = expandedRefills.has(medication.refillId || `refill-${index}`);
                                                const medImageUrls = medication.imageUrls || [];
                                                const medFileUrls = medication.fileUrls || [];

                                                return (
                                                    <div key={medication.refillId || index} className="border rounded-lg overflow-hidden bg-white">
                                                        {/* Collapsible Header */}
                                                        <button
                                                            onClick={() => toggleRefillExpansion(medication.refillId || `refill-${index}`)}
                                                            className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                                                        >
                                                            <div className="flex-1 text-left">
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-sm font-medium text-gray-500">Medication {index + 1}</span>
                                                                </div>
                                                                <h3 className={`text-md font-medium mt-1 ${medication.status === "rejected" ? "text-red-500 line-through" : "text-gray-900"}`}>{medication.name}</h3>
                                                            </div>
                                                            <div className="ml-4 flex gap-2">
                                                                {medication.status === "rejected" ? (
                                                                    <Badge
                                                                        variant="destructive"
                                                                        className="text-white"
                                                                    >
                                                                        Rejected
                                                                    </Badge>
                                                                ) : null}
                                                                {isExpanded ? (
                                                                    <ChevronUp className="w-5 h-5 text-gray-500" />
                                                                ) : (
                                                                    <ChevronDown className="w-5 h-5 text-gray-500" />
                                                                )}
                                                            </div>
                                                        </button>

                                                        {/* Collapsible Content */}
                                                        {isExpanded && (
                                                            <div className="px-4 pb-4 border-t bg-gray-50">
                                                                {/* Medication Details */}
                                                                <div className="mt-4">
                                                                    <RefillCard
                                                                        medicationName={medication.name}
                                                                        medicationDosage={medication.dosage}
                                                                        medicationQuantity={medication.refills?.toString() || ''}
                                                                        medicationVitamin={medication.vitamin}
                                                                    />
                                                                </div>

                                                                {/* Conditional rendering based on program type */}
                                                                {program === "urgent-care" ? (
                                                                    /* Lab Documents and Images Grid for Urgent Care */
                                                                    <ImagesDocsSectionWhite
                                                                        imageUrls={medImageUrls}
                                                                        fileUrls={medFileUrls}
                                                                        onImageClick={handleImageClick}
                                                                    />
                                                                ) : program === "weight-loss" ? (
                                                                    /* Side Effects Section for Weight Loss */
                                                                    <div className="w-full mt-4">
                                                                        <div className="bg-white rounded-lg p-4 min-h-[200px] flex flex-col">
                                                                            <h3 className="text-lg font-semibold mb-4">Side Effects</h3>
                                                                            {data?.sideEffects ? (
                                                                                <div className="flex-1 space-y-3">
                                                                                    {(() => {
                                                                                        const sideEffects = data.sideEffects as Record<string, any>;
                                                                                        const reportedEffects: string[] = [];

                                                                                        // Map of side effect keys to display names
                                                                                        const effectLabels: Record<string, string> = {
                                                                                            nausea: 'Nausea',
                                                                                            diarrhea: 'Diarrhea',
                                                                                            constipation: 'Constipation',
                                                                                            headache: 'Headache',
                                                                                            none: 'None'
                                                                                        };

                                                                                        // Collect all true side effects
                                                                                        Object.keys(effectLabels).forEach(key => {
                                                                                            if (sideEffects[key] === true && effectLabels[key]) {
                                                                                                reportedEffects.push(effectLabels[key]);
                                                                                            }
                                                                                        });

                                                                                        const hasReportedEffects = reportedEffects.length > 0;
                                                                                        const hasOtherSymptoms = sideEffects.other_symptoms && sideEffects.other_symptoms.trim() !== '';

                                                                                        if (!hasReportedEffects && !hasOtherSymptoms) {
                                                                                            return (
                                                                                                <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                                                                                    <span className="text-3xl mb-2">📋</span>
                                                                                                    <span className="text-sm text-center px-2">No side effects reported</span>
                                                                                                </div>
                                                                                            );
                                                                                        }

                                                                                        return (
                                                                                            <>
                                                                                                {hasReportedEffects && (
                                                                                                    <div>
                                                                                                        <p className="text-sm font-medium text-gray-700 mb-2">Reported Symptoms:</p>
                                                                                                        <div className="flex flex-wrap gap-2 bg-white rounded-md p-1 border border-gray-200">
                                                                                                            {reportedEffects.map((effect, idx) => (
                                                                                                                <li
                                                                                                                    key={idx}
                                                                                                                    className="px-3 py-1.5 rounded-full text-sm list-disc list-inside"
                                                                                                                    style={{ display: 'list-item' }}
                                                                                                                >
                                                                                                                    {effect}
                                                                                                                </li>
                                                                                                            ))}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}

                                                                                                {hasOtherSymptoms && (
                                                                                                    <div>
                                                                                                        <p className="text-sm font-medium text-gray-700 mb-2">Additional Symptoms:</p>
                                                                                                        <div className="bg-white rounded-md py-2 px-4 border border-gray-200">
                                                                                                            <ul className="list-disc list-inside space-y-1">
                                                                                                                {sideEffects.other_symptoms
                                                                                                                    .split('\n')
                                                                                                                    .map((symptom: string, idx: number) => {
                                                                                                                        const trimmed = symptom.trim();
                                                                                                                        if (!trimmed) return null;
                                                                                                                        return (
                                                                                                                            <li key={idx}>
                                                                                                                                <span className="text-sm text-gray-700 whitespace-pre-wrap">{trimmed}</span>
                                                                                                                            </li>
                                                                                                                        );
                                                                                                                    })}
                                                                                                            </ul>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                )}
                                                                                            </>
                                                                                        );
                                                                                    })()}
                                                                                </div>
                                                                            ) : (
                                                                                <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                                                                    <span className="text-3xl mb-2">📋</span>
                                                                                    <span className="text-sm text-center px-2">No side effects reported</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                ) : null}

                                                                {/* Reject button for this specific refill */}
                                                                <div className="mt-4 pt-4 border-t">
                                                                    <Button
                                                                        className="w-full !bg-[#E12121] !py-[10px] px-[16px] !rounded-[8px]"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            openRejectModal(medication.refillId, { name: medication.name, dosage: medication.dosage });
                                                                        }}
                                                                        disabled={medication.status === "rejected"}
                                                                    >
                                                                        {medication.status === "rejected" ? "Refill Rejected" : "Reject This "}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        // Single refill - non-collapsible
                                        <>
                                            <RefillCard
                                                medicationName={!Array.isArray(data.medications) ? data.medications.name : ''}
                                                medicationDosage={!Array.isArray(data.medications) ? data.medications.dosage : ''}
                                                medicationQuantity={!Array.isArray(data.medications) ? data.medications.refills?.toString() || '' : ''}
                                                medicationVitamin={!Array.isArray(data.medications) ? data.medications.vitamin : ''}
                                            />

                                            {/* Conditional rendering based on program type */}
                                            {program === "urgent-care" ? (
                                                /* Lab Documents and Images Grid for Urgent Care */
                                                <ImagesDocsSection
                                                    imageUrls={data?.imageUrls || []}
                                                    fileUrls={data?.fileUrls || []}
                                                    onImageClick={handleImageClick}
                                                />
                                            ) : program === "weight-loss" ? (
                                                /* Side Effects Section for Weight Loss */
                                                <div className="w-full mt-6">
                                                    <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] flex flex-col">
                                                        <h3 className="text-lg font-semibold mb-4">Side Effects</h3>
                                                        {data?.sideEffects ? (
                                                            <div className="flex-1 space-y-3">
                                                                {(() => {
                                                                    const sideEffects = data.sideEffects as Record<string, any>;
                                                                    const reportedEffects: string[] = [];

                                                                    // Map of side effect keys to display names
                                                                    const effectLabels: Record<string, string> = {
                                                                        nausea: 'Nausea',
                                                                        diarrhea: 'Diarrhea',
                                                                        constipation: 'Constipation',
                                                                        headache: 'Headache',
                                                                        none: 'None'
                                                                    };

                                                                    // Collect all true side effects
                                                                    Object.keys(effectLabels).forEach(key => {
                                                                        if (sideEffects[key] === true && effectLabels[key]) {
                                                                            reportedEffects.push(effectLabels[key]);
                                                                        }
                                                                    });

                                                                    const hasReportedEffects = reportedEffects.length > 0;
                                                                    const hasOtherSymptoms = sideEffects.other_symptoms && sideEffects.other_symptoms.trim() !== '';

                                                                    if (!hasReportedEffects && !hasOtherSymptoms) {
                                                                        return (
                                                                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                                                                <span className="text-3xl mb-2">📋</span>
                                                                                <span className="text-sm text-center px-2">No side effects reported</span>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <>
                                                                            {hasReportedEffects && (
                                                                                <div>
                                                                                    <p className="text-sm font-medium text-gray-700 mb-2">Reported Symptoms:</p>
                                                                                    <div className="flex flex-wrap gap-2 bg-white rounded-md p-1 border border-gray-200">
                                                                                        {reportedEffects.map((effect, idx) => (
                                                                                            <li
                                                                                                key={idx}
                                                                                                className="px-3 py-1.5 rounded-full text-sm list-disc list-inside"
                                                                                                style={{ display: 'list-item' }}
                                                                                            >
                                                                                                {effect}
                                                                                            </li>
                                                                                        ))}
                                                                                    </div>
                                                                                </div>
                                                                            )}

                                                                            {hasOtherSymptoms && (
                                                                                <div>
                                                                                    <p className="text-sm font-medium text-gray-700 mb-2">Additional Symptoms:</p>
                                                                                    <div className="bg-white rounded-md py-2 px-4 border border-gray-200">
                                                                                        <ul className="list-disc list-inside space-y-1">
                                                                                            {sideEffects.other_symptoms
                                                                                                .split('\n')
                                                                                                .map((symptom: string, idx: number) => {
                                                                                                    const trimmed = symptom.trim();
                                                                                                    if (!trimmed) return null;
                                                                                                    return (
                                                                                                        <li key={idx}>
                                                                                                            <span className="text-sm text-gray-700 whitespace-pre-wrap">{trimmed}</span>
                                                                                                        </li>
                                                                                                    );
                                                                                                })}
                                                                                        </ul>
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    );
                                                                })()}
                                                            </div>
                                                        ) : (
                                                            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 py-6">
                                                                <span className="text-3xl mb-2">📋</span>
                                                                <span className="text-sm text-center px-2">No side effects reported</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : null}
                                        </>
                                    )}
                                </>
                            )}

                            {/* Assigned Actions Section */}
                            {(() => {
                                const tasks = recommendedTasks ?? [];
                                const hasAssignedActions = Array.isArray(tasks) && tasks.length > 0;
                                const isRejected = (data as any)?.status === "rejected";

                                const humanize = (s?: string) =>
                                    s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Task";

                                const getTaskTitle = (task: any) => {
                                    if (task?.type === "medication_payment") return "Prescribed Medication";
                                    if (task?.type === "appointment_followup") return "Pay for Appointment";
                                    return humanize(task?.type);
                                };

                                const formatDate = (d?: string | number) => {
                                    if (!d) return "-";
                                    const dt = new Date(d);
                                    if (isNaN(dt.getTime())) return "-";
                                    return dt.toLocaleDateString("en-GB", {
                                        day: "numeric",
                                        month: "long",
                                        year: "numeric",
                                    });
                                };

                                return (
                                    <div className="flex flex-col gap-3 w-full text-left rounded-[16px] border border-[#0404041A] pb-4 px-4 pt-4 mt-6">
                                        <div className="flex items-center justify-between w-full">
                                            <h2 className="font-medium text-[#1A202C]">
                                                {program === "urgent-care" ? "Assigned actions" : "Recommended actions"}
                                            </h2>
                                            {program === "weight-loss" && (
                                                <Button
                                                    className="text-white bg-[#4272DD] w-fit rounded-[8px] shadow-none border border-none hover:bg-[#4272DD]"
                                                    onClick={handleAddMedication}
                                                >
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    <p>Add medication</p>
                                                </Button>
                                            )}
                                        </div>

                                        {hasAssignedActions || isRejected ? (
                                            <div className="space-y-4">
                                                {/* Show rejected refill as an assigned action */}
                                                {isRejected && (data as any)?.rejectionReason && (
                                                    <div className="bg-[#F7F8FA] rounded-[8px] p-4">
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                                                                    <X className="w-3 h-3 text-white" />
                                                                </div>
                                                                <div>
                                                                    <h3 className="font-medium text-red-600">
                                                                        Rejected Refill Request
                                                                    </h3>
                                                                    <p className="text-sm text-[#6B7280]">
                                                                        Rejected: {formatDate(data?.requestedAt)}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="bg-white rounded-md p-2 space-y-2">
                                                            <h4 className="font-medium text-[#1A202C]">
                                                                {(data as any)?.rejectionReason?.reason || "Rejection Reason"}
                                                            </h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                                                                {data?.isBatch && Array.isArray(data?.medications) ? (
                                                                    // Show all medications for batch
                                                                    data.medications.map((med: any, idx: number) => (
                                                                        <div key={idx} className="md:col-span-2 border-b pb-2 mb-2">
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                                <div>
                                                                                    <span className="font-medium">Medication: </span>
                                                                                    <span className="line-through text-red-600">
                                                                                        {med?.name ?? "-"}
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="font-medium">Dosage: </span>
                                                                                    {med?.dosage ?? "-"}
                                                                                </div>
                                                                                <div>
                                                                                    <span className="font-medium">Refills: </span>
                                                                                    {med?.refills ?? "-"}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                ) : (
                                                                    <>
                                                                        <div>
                                                                            <span className="font-medium">Medication: </span>
                                                                            <span className="line-through text-red-600">
                                                                                {!Array.isArray(data?.medications) ? data?.medications?.name ?? "-" : "-"}
                                                                            </span>
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Dosage: </span>
                                                                            {!Array.isArray(data?.medications) ? data?.medications?.dosage ?? "-" : "-"}
                                                                        </div>
                                                                        <div>
                                                                            <span className="font-medium">Refills: </span>
                                                                            {!Array.isArray(data?.medications) ? data?.medications?.refills ?? "-" : "-"}
                                                                        </div>
                                                                    </>
                                                                )}
                                                                <div className="md:col-span-2">
                                                                    <span className="font-medium">Rejection Details: </span>
                                                                    <span>{(data as any)?.rejectionReason?.details ?? "-"}</span>
                                                                </div>
                                                                <div>
                                                                    <span className="font-medium">Request Date: </span>
                                                                    {formatDate(data?.requestedAt)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Show recommended tasks */}
                                                {tasks.map((task: any, index: number) => {
                                                    const isMedicationTask = task?.type === "medication_payment";
                                                    const meds = task?.prescription?.medication ?? [];
                                                    const isTaskCompleted = task?.status === "completed";
                                                    const isTaskPending = task?.status === "pending";
                                                    const hasPrescription = task?.prescription && meds.length > 0;
                                                    // Only show green if task is completed (not pending) and has prescription for medication tasks
                                                    const showGreen = isMedicationTask
                                                        ? (hasPrescription && !isTaskPending && isTaskCompleted)
                                                        : isTaskCompleted;

                                                    return (
                                                        <div key={task?.task_id || index} className="bg-[#F7F8FA] rounded-[8px] p-4">
                                                            <div className="flex items-start justify-between mb-3">
                                                                <div className="flex items-center gap-3">
                                                                    {showGreen ? (
                                                                        <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                                                                            <Check className="w-3 h-3 text-white" />
                                                                        </div>
                                                                    ) : (
                                                                        <Circle className="w-5 h-5 text-blue-500" />
                                                                    )}
                                                                    <div>
                                                                        <h3
                                                                            className={cn(
                                                                                "font-medium",
                                                                                showGreen ? "text-green-600" : "text-[#1A202C]"
                                                                            )}
                                                                        >
                                                                            {getTaskTitle(task)}
                                                                        </h3>
                                                                        <p className="text-sm text-[#6B7280]">
                                                                            Assigned: {formatDate(task?.created_at)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Medication details */}
                                                            {isMedicationTask && Array.isArray(meds) && meds.length > 0 && (
                                                                <div className="space-y-3">
                                                                    {meds.map((m: any, i: number) => (
                                                                        <div
                                                                            key={`${m?.name || "med"}-${i}`}
                                                                            className="bg-white rounded-md p-2 space-y-2"
                                                                        >
                                                                            <h4 className="font-medium text-[#1A202C]">
                                                                                {m?.name || "-"}
                                                                            </h4>
                                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#6B7280]">
                                                                                <div>
                                                                                    <span className="font-medium">Dosage: </span>
                                                                                    {m?.dosage ?? "-"}
                                                                                </div>
                                                                                <div>
                                                                                    <span className="font-medium">Duration: </span>
                                                                                    {typeof m?.duration === "number" ? `${m.duration} days` : (m?.duration ?? "-")}
                                                                                </div>
                                                                                <div className="md:col-span-2">
                                                                                    <span className="font-medium">Instructions: </span>
                                                                                    {Array.isArray(m?.instructions) ? (
                                                                                        <ul className="list-disc ml-5">
                                                                                            {m.instructions.map((ins: string, idx: number) => (
                                                                                                <li key={idx}>{ins}</li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    ) : (
                                                                                        <span>{m?.instructions ?? "-"}</span>
                                                                                    )}
                                                                                </div>
                                                                                <div>
                                                                                    <span className="font-medium">Refills: </span>
                                                                                    {m?.refills ?? "-"}
                                                                                </div>
                                                                                <div>
                                                                                    <span className="font-medium">Effective date: </span>
                                                                                    {formatDate(m?.effective_date)}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}

                                                            {/* Non-medication task details */}
                                                            {!isMedicationTask && (
                                                                <div className="text-sm text-[#6B7280] bg-white rounded-md p-2 space-y-2">
                                                                    <div>
                                                                        <span className="font-medium">Price: </span>
                                                                        <span>{task?.cost ?? "-"}</span>
                                                                    </div>
                                                                    {task?.instructions && (
                                                                        <div>
                                                                            <span className="font-medium">Instructions: </span>
                                                                            <span>{task.instructions}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="bg-[#F7F8FA] rounded-[8px] py-12 px-6 flex flex-col items-center justify-center text-center">
                                                <p className="font-medium text-[#1A202C] mb-1">No assigned actions</p>
                                                <p className="text-sm text-[#6B7280]">
                                                    When actions are assigned, they will appear here.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}
                            {/* Action footer moved below to be fixed at bottom */}
                        </>
                    )}
                </div>

                {/* Fixed bottom action footer via flex layout */}
                <div className="mt-auto py-4 border-t">
                    {isLoading ? (
                        <div className="flex gap-2">
                            <Skeleton className="h-11 w-[133px] rounded-md" />
                            <Skeleton className="h-11 flex-1 rounded-md" />
                        </div>
                    ) : (
                        <>
                            {program === "weight-loss" && data?.paymentStatus !== "paid" && (
                                <div className="text-sm text-[#1A202C] bg-[#EBF0FB] p-2 rounded-md flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <Info className="text-[#4272DD] size-[16px]" />
                                        <span>Payment required before sending to pharmacy.</span>
                                    </div>
                                    <Button
                                        className="!bg-[#2563EB] !text-white !rounded-[6px] !py-1 !px-3 !text-xs"
                                        onClick={() => {
                                            toast.info('Payment reminder sent to patient');
                                        }}
                                    >
                                        Remind to Pay
                                    </Button>
                                </div>
                            )}
                            <div className="flex gap-2">
                                {/* Only show reject button for non-batch refills */}
                                {!data?.isBatch && (
                                    <Button
                                        className={`w-[133px] !bg-[#E12121] !py-[12px] px-[16px] ${program === "urgent-care" ? "h-[44px] rounded" : "!rounded-[8px]"}`}
                                        onClick={() => openRejectModal()}
                                    >
                                        Reject Refill
                                    </Button>
                                )}
                                {program === "urgent-care" ? (
                                    <div className="flex-1">
                                        <DosespotButton
                                            userDosespotId={userDosespotId ?? data?.userDosespotId}
                                            sessionId={sessionId}
                                            encounterId={encounterId}
                                            urgent_care_onboarded={urgent_care_onboarded}
                                            onSuccess={() => { }}
                                        />
                                    </div>
                                ) : (
                                    <Button
                                        className={cn(
                                            "!text-white w-full !rounded-[8px] !border-none !py-[14px]",
                                            program === "weight-loss" && data?.paymentStatus !== "paid" && "!cursor-not-allowed !opacity-50",
                                            shouldDisableSendToPharmacy() && "!cursor-not-allowed !opacity-50"
                                        )}
                                        onClick={handleApproveRefill}
                                        disabled={(program === "weight-loss" && data?.paymentStatus !== "paid") || shouldDisableSendToPharmacy()}
                                    >
                                        {(approveRefillMutation.isPending || sendInvoiceMutation.isPending) ? (
                                            <Loader2 className="w-4 h-4 text-white" />
                                        ) : (
                                            <>
                                                {program === "weight-loss" ? (shouldShowResendText() ? "Resend to Pharmacy" : "Send to Pharmacy") : "Approve Refill"}
                                            </>
                                        )}
                                    </Button>
                                )}
                            </div>
                        </>
                    )}
                </div>

                <Dialog open={showRejectModal} onOpenChange={closeRejectModal}>
                    <DialogContent className="rounded-[24px]">
                        <DialogHeader className="flex items-center justify-center">
                            <DialogTitle className=" text-xl font-medium">Reject Request</DialogTitle>
                        </DialogHeader>
                        <div>
                            <p className="flex items-center mb-4 text-xl font-medium justify-center text-center">
                                You are rejecting the refill request for {rejectingMedication
                                    ? `${rejectingMedication.name} ${rejectingMedication.dosage}`
                                    : data?.isBatch && Array.isArray(data?.medications)
                                        ? `${data.medications.length} medication(s)`
                                        : !Array.isArray(data?.medications) ? `${data?.medications?.name || ''} ${data?.medications?.dosage || ''}` : ''} for {data?.patientName}.
                            </p>
                            <label className="text-[14px] mt-4 text-[#1A202C]">
                                <Select
                                    value={rejectReason}
                                    onValueChange={setRejectReason}
                                >
                                    <SelectTrigger className="w-full rounded-[8px] border h-[44px] p-2 mt-2 mb-4">
                                        <SelectValue placeholder="Select a reason" />
                                    </SelectTrigger>

                                    <SelectContent>
                                        <SelectGroup>
                                            {/* <SelectLabel>Rejection Reasons</SelectLabel> */}
                                            <SelectItem value="Cannot prescribe this medication">Cannot prescribe this medication</SelectItem>
                                            {program === "urgent-care" && (
                                                <SelectItem value="Need more information">Need more information</SelectItem>
                                            )}
                                            {program === "weight-loss" && (
                                                <SelectItem value="Medication Completed / Discontinued">Medication Completed / Discontinued</SelectItem>
                                            )}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                                <label className="text-[14px] mt-4 text-[#1A202C]">Please provide additional details:</label>
                                <textarea
                                    value={rejectDetails}
                                    onChange={e => setRejectDetails(e.target.value)}
                                    maxLength={200}
                                    className="w-full rounded-[8px] border h-[164px] p-2 mt-2"
                                />
                            </label>
                            {reasonError && <div className="text-red-500">{reasonError}</div>}
                            <div className="flex justify-end gap-2 mt-4">
                                <Button onClick={closeRejectModal} className="bg-transparent hover:bg-transparent border border-[#DCDCDE] rounded-[8px] text-[#202020CC]">Cancel</Button>
                                <Button
                                    className="!bg-[#E12121] rounded-[8px] text-white"
                                    disabled={rejectRefillMutation.isPending}
                                    onClick={async () => {
                                        if (!rejectReason.trim()) {
                                            setReasonError("Reason is required.");
                                            return;
                                        }
                                        if (!rejectDetails.trim()) {
                                            setReasonError("Additional details are required.");
                                            return;
                                        }
                                        setReasonError("");
                                        await handleRejectRefill(rejectReason, rejectDetails, rejectingRefillId || undefined);
                                        closeRejectModal();
                                    }}
                                >
                                    {rejectRefillMutation.isPending ? (
                                        <Loader2 className="w-4 h-4 text-white animate-spin" />
                                    ) : (
                                        "Confirm"
                                    )}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>

                {/* Image Modal */}
                {selectedImage && (
                    <ImageModal
                        isOpen={!!selectedImage}
                        onClose={handleCloseImageModal}
                        imageUrl={selectedImage.url}
                        imageIndex={selectedImage.index}
                        totalImages={data?.imageUrls?.length || 0}
                    />
                )}

                {/* Prescription Modal for weight-loss */}
                {openPrescriptionModal && data && (
                    <PrescriptionModal
                        open={openPrescriptionModal}
                        patientId={data.patientId}
                        encounterId={encounterId}
                        onOpenChange={setOpenPrescriptionModal}
                        refillId={data.refillId}
                        prescriptionId={data.prescriptionId}
                    />
                )}
            </SheetContent>
        </Sheet>
    )
}